/**
 * Trainer · Dijital Antrenör
 *
 * worker/src/handlers/trainer.ts'in Mini App muadili.
 *  - Top: srsSummary (toplam / due / mastered + kutu chip'leri)
 *  - "Bugün <N> tekrar" CTA → in-page review session view
 *  - Liste: paginated srsList (limit=20, offset state, prev/next)
 *  - Review session UI: kart ya da soru sun, İyi Hatırladım / Tekrar Etmem
 *    Gerek butonları ile srsReview, sıradaki due item'a geçer ya da 404'te
 *    summary'ye döner.
 *
 * Algomed Chakra semantic token'ları (bgCard / borderPrimary / accent ...).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Progress,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { api, type SrsListItem, type SrsNextResp, type SrsSummaryResp } from '../lib/api';
import { haptic, setMainButton } from '../telegram';
import { BRAND, ICON } from '@algobot/shared';

type Mode = 'home' | 'review';

const PAGE_SIZE = 20;

function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function formatNext(iso: string): string {
  const d = new Date(iso);
  const days = Math.round((d.getTime() - Date.now()) / 86400000);
  if (days <= 0) return 'bugün';
  if (days === 1) return 'yarın';
  return `${days} gün sonra`;
}

export default function TrainerPage() {
  const nav = useNavigate();
  const [mode, setMode] = useState<Mode>('home');

  // Summary
  const [summary, setSummary] = useState<SrsSummaryResp | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // List (paginated)
  const [offset, setOffset] = useState(0);
  const [list, setList] = useState<SrsListItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Review session
  const [current, setCurrent] = useState<SrsNextResp | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<{ status?: number; message: string } | null>(null);
  const [flipped, setFlipped] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);

  const loadSummary = async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const s = await api.srsSummary();
      setSummary(s);
    } catch (err: any) {
      setSummaryError(
        err?.status === 401
          ? 'Telegram içinde aç.'
          : 'Antrenör verisi alınamadı.',
      );
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadList = async (nextOffset: number) => {
    setListLoading(true);
    setListError(null);
    try {
      const rows = await api.srsList({ offset: nextOffset, limit: PAGE_SIZE });
      setList(rows);
      setOffset(nextOffset);
    } catch {
      setListError('Liste alınamadı.');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    loadList(0);
  }, []);

  // ── Review actions ──────────────────────────────────────
  const fetchNext = async (resetCount = false) => {
    setReviewLoading(true);
    setReviewError(null);
    setFlipped(false);
    try {
      const next = await api.srsNext();
      setCurrent(next);
      if (resetCount) setCompletedCount(0);
    } catch (err: any) {
      setCurrent(null);
      if (err?.status === 404) {
        setReviewError({ status: 404, message: 'Tekrar tamamlandı! Bugün için bekleyen kayıt yok.' });
      } else if (err?.status === 429) {
        setReviewError({ status: 429, message: 'Günlük SRS tekrar limitin doldu. Plan yükselterek limiti kaldırabilirsin.' });
      } else {
        setReviewError({ message: 'Sıradaki tekrar alınamadı.' });
      }
    } finally {
      setReviewLoading(false);
    }
  };

  const enterReview = async () => {
    haptic('select');
    setMode('review');
    await fetchNext(true);
  };

  const exitReview = () => {
    haptic('select');
    setMode('home');
    setCurrent(null);
    setReviewError(null);
    setFlipped(false);
    setCompletedCount(0);
    // Summary'yi yenile (kutular değişmiş olabilir)
    loadSummary();
    loadList(offset);
  };

  const submitReview = async (correct: boolean) => {
    if (!current || submitting) return;
    setSubmitting(true);
    try {
      // claimToken'i srsNext response'undan al ve geri gonder — race koruma
      const res = await api.srsReview(current.srsId, correct, current.claimToken);
      haptic(correct ? 'success' : 'warning');
      setCompletedCount((c) => c + 1);
      void res;
      await fetchNext(false);
    } catch (err: any) {
      haptic('error');
      if (err?.status === 429) {
        setCurrent(null);
        // Backend zengin payload doner — used/limit/resetAt body'de var.
        const body = err?.body || {};
        setReviewError({
          status: 429,
          message: typeof body.limit === 'number'
            ? `Günlük SRS tekrar limitin (${body.used ?? '-'} / ${body.limit}) doldu. Plan yükselterek limiti kaldırabilirsin.`
            : 'Günlük SRS tekrar limitin doldu.',
        });
      } else if (err?.status === 409 || err?.code === 'SRS_STALE') {
        // Bu kart baska bir cihaz tarafindan tekrar edilmis → kota harcanmadi.
        // Sessizce sıradaki kartı çek.
        await fetchNext(false);
      } else {
        setReviewError({ message: 'Kaydedilemedi.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const removeCurrent = async () => {
    if (!current || submitting) return;
    setSubmitting(true);
    try {
      await api.srsRemove(current.srsId);
      haptic('select');
      await fetchNext(false);
    } catch {
      haptic('error');
      setReviewError({ message: 'Çıkarılamadı.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Telegram MainButton entegrasyonu ─────────────────────
  const dueCount = summary?.due ?? 0;
  useEffect(() => {
    if (mode !== 'home') return;
    if (summaryLoading) return;
    if (!summary || summary.due <= 0) return;
    const cleanup = setMainButton(`Tekrar Et (${summary.due})`, enterReview);
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, summaryLoading, summary?.due]);

  // ── Render: review mode ─────────────────────────────────
  if (mode === 'review') {
    return (
      <Container maxW="md" py={4} px={3}>
        <ReviewSession
          loading={reviewLoading}
          submitting={submitting}
          current={current}
          flipped={flipped}
          completedCount={completedCount}
          onFlip={() => {
            haptic('select');
            setFlipped((v) => !v);
          }}
          onSubmit={submitReview}
          onRemove={removeCurrent}
          error={reviewError}
          onExit={exitReview}
          goSubscription={() => nav('/subscription')}
        />
      </Container>
    );
  }

  // ── Render: home/summary mode ────────────────────────────
  return (
    <Container maxW="md" py={4} px={3}>
      <VStack spacing={3} align="stretch">
        <HStack spacing={2} align="center">
          <Text fontSize="2xl" lineHeight="1" color="successIcon" fontWeight="bold">
            {ICON.trainer}
          </Text>
          <Heading size="md" color="textPrimary">
            Dijital Antrenör
          </Heading>
        </HStack>
        <Text color="textSecondary" fontSize="sm">
          Leitner aralıklı tekrar · bildiklerin yukarı kutuya, bilmediklerin 1. kutuya düşer.
        </Text>

        {/* SUMMARY CARD */}
        <Box
          bg="bgCard"
          borderWidth="1px"
          borderColor="borderPrimary"
          borderRadius="xl"
          boxShadow="cardElevated"
          p={4}
        >
          {summaryLoading ? (
            <VStack p={4}>
              <Spinner color="accent" />
              <Text fontSize="sm" color="textSecondary">Yükleniyor…</Text>
            </VStack>
          ) : summaryError ? (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              {summaryError}
            </Alert>
          ) : summary ? (
            <VStack align="stretch" spacing={3}>
              <SimpleGrid columns={3} spacing={2}>
                <SummaryStat label="Toplam" value={summary.total} accent="textPrimary" glyph={ICON.bullet} />
                <SummaryStat label="Bugün" value={summary.due} accent="successIcon" glyph={ICON.srs} />
                <SummaryStat label="Öğrenilen" value={summary.mastered} accent="accent" glyph={ICON.ok} />
              </SimpleGrid>

              {summary.byBox.length > 0 ? (
                <Box>
                  <Text
                    fontSize="10px"
                    color="textMuted"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    mb={1.5}
                  >
                    Kutu Dağılımı
                  </Text>
                  <Wrap spacing={1.5}>
                    {summary.byBox.map((b) => (
                      <WrapItem key={b.box}>
                        <Badge
                          colorScheme="blue"
                          borderRadius="full"
                          px={2.5}
                          py={1}
                          fontSize="11px"
                          textTransform="none"
                        >
                          Kutu {b.box} · {b.count}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              ) : (
                <Text fontSize="sm" color="textSecondary" fontStyle="italic">
                  Henüz Dijital Antrenörde kayıt yok. Soru/Flashcard çözerken
                  "Dijital Antrenöre Ekle" butonuna dokun.
                </Text>
              )}

              <Button
                colorScheme="brand"
                size="md"
                isDisabled={dueCount <= 0}
                onClick={enterReview}
              >
                {dueCount > 0 ? `Bugün ${dueCount} tekrar · Tekrar Et` : 'Bugün için tekrar yok'}
              </Button>
            </VStack>
          ) : null}
        </Box>

        {/* LIST */}
        <Box
          bg="bgCard"
          borderWidth="1px"
          borderColor="borderPrimary"
          borderRadius="xl"
          boxShadow="cardElevated"
          p={3}
        >
          <HStack justify="space-between" align="center" mb={2}>
            <Text fontWeight="bold" color="textPrimary" fontSize="sm">
              Tüm Kayıtlar
            </Text>
            <Text fontSize="11px" color="textMuted">
              {offset + 1}–{offset + list.length}
            </Text>
          </HStack>

          {listLoading ? (
            <VStack p={4}><Spinner size="sm" color="accent" /></VStack>
          ) : listError ? (
            <Alert status="warning" borderRadius="md"><AlertIcon />{listError}</Alert>
          ) : list.length === 0 ? (
            <Text fontSize="sm" color="textSecondary" fontStyle="italic" py={2}>
              Bu sayfada gösterilecek kayıt yok.
            </Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {list.map((row) => (
                <ListRow key={row.id} row={row} />
              ))}
            </VStack>
          )}

          <HStack mt={3} justify="space-between">
            <Button
              size="sm"
              variant="outline"
              isDisabled={offset === 0 || listLoading}
              onClick={() => loadList(Math.max(0, offset - PAGE_SIZE))}
            >
              {ICON.back} Önceki
            </Button>
            <Button
              size="sm"
              variant="outline"
              isDisabled={list.length < PAGE_SIZE || listLoading}
              onClick={() => loadList(offset + PAGE_SIZE)}
            >
              Sonraki {ICON.forward}
            </Button>
          </HStack>
        </Box>

        <Button variant="ghost" onClick={() => nav('/')}>
          {BRAND.menuLabels.home}
        </Button>
      </VStack>
    </Container>
  );
}

// ──────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────

function SummaryStat({ label, value, accent, glyph }: { label: string; value: number; accent: string; glyph: string }) {
  return (
    <Box
      bg="bgSecondary"
      borderWidth="1px"
      borderColor="borderPrimary"
      borderRadius="lg"
      px={2.5}
      py={2}
    >
      <HStack spacing={1.5} mb={0.5}>
        <Text fontSize="md" lineHeight="1" color={accent} fontWeight="bold">
          {glyph}
        </Text>
        <Text
          fontSize="10px"
          color="textMuted"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.05em"
          noOfLines={1}
        >
          {label}
        </Text>
      </HStack>
      <Text fontWeight="bold" fontSize="lg" lineHeight="1.1" color="textPrimary">
        {value}
      </Text>
    </Box>
  );
}

function ListRow({ row }: { row: SrsListItem }) {
  const kindLabel = row.kind === 'question' ? 'Soru' : row.kind === 'flashcard' ? 'Flashcard' : 'Konu';
  const kindGlyph =
    row.kind === 'question' ? BRAND.emojiContent.question
    : row.kind === 'flashcard' ? BRAND.emojiContent.flashcard
    : BRAND.emojiContent.topic;

  return (
    <Box
      bg="bgSecondary"
      borderWidth="1px"
      borderColor="borderPrimary"
      borderRadius="lg"
      px={3}
      py={2}
    >
      <HStack spacing={2} align="start">
        <Text fontSize="md" lineHeight="1" color="accent" fontWeight="bold" pt={0.5}>
          {kindGlyph}
        </Text>
        <Box flex="1" minW={0}>
          <HStack spacing={1.5} mb={0.5}>
            <Badge colorScheme="blue" fontSize="10px" textTransform="none">Kutu {row.boxNumber}</Badge>
            <Text fontSize="10px" color="textMuted">{kindLabel}</Text>
            {row.isMastered ? (
              <Badge colorScheme="green" fontSize="10px" textTransform="none">öğrenildi</Badge>
            ) : (
              <Text fontSize="10px" color="textMuted">· {formatNext(row.nextReviewAt)}</Text>
            )}
          </HStack>
          <Text fontSize="13px" color="textPrimary" noOfLines={2} lineHeight="1.3">
            {stripHtml(row.title) || '(başlıksız)'}
          </Text>
        </Box>
      </HStack>
    </Box>
  );
}

interface ReviewSessionProps {
  loading: boolean;
  submitting: boolean;
  current: SrsNextResp | null;
  flipped: boolean;
  completedCount: number;
  onFlip: () => void;
  onSubmit: (correct: boolean) => void;
  onRemove: () => void;
  error: { status?: number; message: string } | null;
  onExit: () => void;
  goSubscription: () => void;
}

function ReviewSession(props: ReviewSessionProps) {
  const { loading, submitting, current, flipped, completedCount, onFlip, onSubmit, onRemove, error, onExit, goSubscription } = props;

  if (loading && !current) {
    return (
      <VStack p={12} spacing={3}>
        <Spinner size="xl" color="accent" thickness="3px" />
        <Text color="textSecondary" fontSize="sm">Sıradaki tekrar yükleniyor…</Text>
      </VStack>
    );
  }

  if (error) {
    const isDone = error.status === 404;
    const isLimit = error.status === 429;
    return (
      <VStack spacing={3} align="stretch">
        <Box
          bg="bgCard"
          borderWidth="1px"
          borderColor={isDone ? 'successIcon' : 'borderPrimary'}
          borderRadius="xl"
          boxShadow="cardElevated"
          p={5}
        >
          <VStack spacing={3} align="center">
            <Text fontSize="3xl" color={isDone ? 'successIcon' : 'warningIcon'}>
              {isDone ? ICON.ok : ICON.warn}
            </Text>
            <Heading size="md" textAlign="center" color="textPrimary">
              {isDone ? 'Tekrar Tamamlandı' : isLimit ? 'Günlük Limit Doldu' : 'Hata'}
            </Heading>
            <Text fontSize="sm" color="textSecondary" textAlign="center">
              {error.message}
            </Text>
            {completedCount > 0 ? (
              <Badge colorScheme="green" textTransform="none">
                Bu oturumda {completedCount} tekrar yaptın
              </Badge>
            ) : null}
          </VStack>
        </Box>
        <HStack>
          {isLimit ? (
            <Button flex="1" colorScheme="brand" onClick={goSubscription}>
              Planı Yükselt
            </Button>
          ) : null}
          <Button flex="1" variant="outline" onClick={onExit}>
            Özete Dön
          </Button>
        </HStack>
      </VStack>
    );
  }

  if (!current) {
    return (
      <VStack p={6}>
        <Spinner color="accent" />
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <HStack justify="space-between" align="center">
        <HStack spacing={1.5}>
          <Badge colorScheme="blue" textTransform="none">Kutu {current.boxNumber}</Badge>
          <Badge colorScheme="purple" textTransform="none">
            {current.kind === 'question' ? 'Soru' : current.kind === 'flashcard' ? 'Flashcard' : 'Konu'}
          </Badge>
        </HStack>
        <Text fontSize="11px" color="textMuted">+{completedCount} bitti</Text>
      </HStack>

      <Box
        bg="bgCard"
        borderWidth="1px"
        borderColor="borderPrimary"
        borderRadius="xl"
        boxShadow="cardElevated"
        p={4}
      >
        {current.kind === 'question' && <ReviewQuestion item={current.item} />}
        {current.kind === 'flashcard' && (
          <ReviewFlashcard item={current.item} flipped={flipped} onFlip={onFlip} />
        )}
        {current.kind === 'topic' && <ReviewTopic item={current.item} />}
      </Box>

      <SimpleGrid columns={2} spacing={2}>
        <Button
          colorScheme="green"
          size="md"
          isDisabled={submitting}
          onClick={() => onSubmit(true)}
        >
          {ICON.ok} {BRAND.copy.remembered}
        </Button>
        <Button
          colorScheme="red"
          variant="outline"
          size="md"
          isDisabled={submitting}
          onClick={() => onSubmit(false)}
        >
          {ICON.cross} {BRAND.copy.needReview}
        </Button>
      </SimpleGrid>

      <HStack>
        <Button flex="1" variant="ghost" size="sm" onClick={onRemove} isDisabled={submitting}>
          {ICON.minus} Antrenörden çıkar
        </Button>
        <Button flex="1" variant="outline" size="sm" onClick={onExit}>
          Özete Dön
        </Button>
      </HStack>

      {submitting && <Progress size="xs" isIndeterminate colorScheme="brand" />}
    </VStack>
  );
}

function ReviewQuestion({ item }: { item: any }) {
  const [picked, setPicked] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);

  // Yeni soruya geçildiğinde state sıfırlansın
  const sig = useMemo(() => `${item?.id}-${item?.text?.length || 0}`, [item?.id, item?.text]);
  useEffect(() => {
    setPicked('');
    setSubmitted(false);
  }, [sig]);

  if (!item) return null;
  const correct = submitted && picked === item.correctAnswer;

  return (
    <VStack align="stretch" spacing={3}>
      <Box
        fontWeight="semibold"
        color="textPrimary"
        fontSize="md"
        lineHeight="1.4"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.text || '') }}
      />
      <VStack align="stretch" spacing={1.5}>
        {(item.options as Array<{ key: string; text: string }>).map((o) => {
          const isCorrect = submitted && o.key === item.correctAnswer;
          const isWrongPicked = submitted && o.key === picked && picked !== item.correctAnswer;
          return (
            <Box
              key={o.key}
              as="button"
              type="button"
              textAlign="left"
              p={2.5}
              borderRadius="md"
              borderWidth="1px"
              borderColor={
                isCorrect ? 'optionCorrectBorderColor'
                : isWrongPicked ? 'optionIncorrectBorderColor'
                : picked === o.key ? 'accent'
                : 'optionDefaultBorderColor'
              }
              bg={
                isCorrect ? 'optionCorrectBg'
                : isWrongPicked ? 'optionIncorrectBg'
                : 'optionDefaultBg'
              }
              color={isCorrect || isWrongPicked ? 'white' : undefined}
              onClick={() => !submitted && setPicked(o.key)}
              _disabled={{ opacity: 0.7 }}
            >
              <HStack align="start" spacing={2}>
                <Text fontWeight="bold">{o.key})</Text>
                <Box
                  flex="1"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(o.text || '') }}
                />
              </HStack>
            </Box>
          );
        })}
      </VStack>
      {!submitted ? (
        <Button
          size="sm"
          variant="outline"
          isDisabled={!picked}
          onClick={() => setSubmitted(true)}
        >
          Cevabı Göster
        </Button>
      ) : (
        <>
          <Alert status={correct ? 'success' : 'error'} borderRadius="md" fontSize="sm">
            <AlertIcon />
            {correct ? 'Doğru!' : `Doğru cevap: ${item.correctAnswer}`}
          </Alert>
          {item.explanation ? (
            <Box
              bg="bgExplanation"
              borderWidth="1px"
              borderColor="borderExplanation"
              p={3}
              borderRadius="md"
              fontSize="sm"
              color="textPrimary"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.explanation) }}
            />
          ) : null}
        </>
      )}
    </VStack>
  );
}

function ReviewFlashcard({ item, flipped, onFlip }: { item: any; flipped: boolean; onFlip: () => void }) {
  if (!item) return null;
  return (
    <VStack align="stretch" spacing={3}>
      <Box
        minH="180px"
        p={5}
        borderRadius="lg"
        bg={flipped ? 'flashcardBackGradient' : 'flashcardFrontGradient'}
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        textAlign="center"
        onClick={onFlip}
      >
        <Box
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(flipped ? item.backText || '' : item.frontText || ''),
          }}
        />
      </Box>
      <Button size="sm" variant="outline" onClick={onFlip}>
        {flipped ? 'Ön yüze' : 'Cevabı göster'}
      </Button>
    </VStack>
  );
}

function ReviewTopic({ item }: { item: any }) {
  if (!item) return null;
  return (
    <VStack align="start" spacing={2}>
      <Flex
        w="42px"
        h="42px"
        borderRadius="lg"
        bg="bgSecondary"
        borderWidth="1px"
        borderColor="borderSecondary"
        align="center"
        justify="center"
      >
        <Text fontSize="xl" lineHeight="1" color="accent" fontWeight="bold">
          {BRAND.emojiContent.topic}
        </Text>
      </Flex>
      <Heading size="sm" color="textPrimary">{item.name}</Heading>
      <Text fontSize="sm" color="textSecondary">
        Bu konuyu hatırlıyor musun? "İyi Hatırladım" veya "Tekrar Etmem Gerek" ile değerlendir.
      </Text>
    </VStack>
  );
}
